import React, { useRef } from 'react';
import { useChatDagRuntime } from '../../../contexts/ChatDagRuntimeContext';
import type { GroupingDim } from './types';

interface DimDef { id: GroupingDim; label: string; hotkey: string; }
// Flat ('off') removed during the 2026-06-14 dogfood recon: it collapsed
// artifacts behind a count badge anchored to the turn's output marker (off-screen
// at the bottom of a long turn), so artifacts were effectively invisible — while
// turn/round/persona render them cleanly. Default is now 'turn'.
const DIMS: DimDef[] = [
  { id: 'turn', label: 'turn', hotkey: '1' },
  { id: 'phase', label: 'round', hotkey: '2' }, // user label "round", internal id "phase"
  { id: 'persona', label: 'persona', hotkey: '3' },
];

const pillClass = (active: boolean) =>
  `inline-flex items-center gap-1 rounded px-2 py-0.5 text-[11px] transition-colors ${
    active
      ? 'bg-emerald-600 text-white dark:bg-emerald-500'
      : 'text-gray-600 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700'
  }`;

const PillBar: React.FC = () => {
  const { activeDim, setActiveDim } = useChatDagRuntime();
  const focusableRef = useRef<HTMLButtonElement | null>(null);

  // Arrow keys move focus AND select (APG "selection follows focus").
  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;
    e.preventDefault();
    const i = DIMS.findIndex((d) => d.id === activeDim);
    const next = e.key === 'ArrowRight' ? (i + 1) % DIMS.length : (i - 1 + DIMS.length) % DIMS.length;
    setActiveDim(DIMS[next].id);
  };

  return (
    <div
      role="radiogroup"
      aria-label="Group artifacts by dimension"
      title="Group artifacts — or press Alt+1/2/3"
      onKeyDown={onKeyDown}
      className="flex items-center gap-1 rounded-md border border-gray-200 bg-white/95 px-1.5 py-1 shadow-sm dark:border-gray-700 dark:bg-gray-900/95"
    >
      {DIMS.map((d) => (
        <button
          key={d.id}
          type="button"
          role="radio"
          aria-checked={activeDim === d.id}
          aria-keyshortcuts={`Alt+${d.hotkey}`}
          tabIndex={activeDim === d.id ? 0 : -1}
          ref={activeDim === d.id ? focusableRef : undefined}
          onClick={() => setActiveDim(d.id)}
          className={pillClass(activeDim === d.id)}
          data-testid={`pill-${d.id}`}
        >
          <span>{d.label}</span>
          <kbd className="font-mono text-[9px] opacity-60" aria-hidden="true">Alt+{d.hotkey}</kbd>
        </button>
      ))}
    </div>
  );
};

export default PillBar;
